import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import './Auth.css'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isValidToken, setIsValidToken] = useState(false)

  // Check if we have a valid token from Supabase
  useEffect(() => {
    const handleAuthToken = async () => {
      try {
        // Get the hash from the URL
        const hash = window.location.hash

        // Parse the hash to get auth parameters
        if (hash) {
          // The hash will contain: #access_token=...&type=recovery&expires_in=...
          const hashParams = new URLSearchParams(hash.substring(1))
          const accessToken = hashParams.get('access_token')
          const type = hashParams.get('type')

          if (accessToken && type === 'recovery') {
            // Set the session with the token from the email link
            const { data, error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: hashParams.get('refresh_token') || ''
            })

            if (sessionError) {
              throw sessionError
            }

            if (data.session) {
              setIsValidToken(true)
              return
            }
          }
        }

        setError('Invalid or expired reset link. Please request a new one.')
      } catch (err) {
        setError('Failed to verify reset link: ' + (err.message || 'Unknown error'))
      }
    }

    handleAuthToken()
  }, [])

  const handleResetPassword = async (e) => {
    e.preventDefault()
    setError('')

    if (!password || !confirmPassword) {
      setError('Please fill in all fields')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    try {
      // Update the user's password
      const { error } = await supabase.auth.updateUser({ password })

      if (error) throw error

      setSuccess(true)
      setPassword('')
      setConfirmPassword('')

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/')
      }, 2000)
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-bg-pattern" />

      <div className="auth-container">
        {/* Brand */}
        <div className="auth-brand">
          <h1 className="auth-logo">Agora</h1>
          <p className="auth-tagline">the digital town square.</p>
        </div>

        {/* Reset Password Card */}
        <div className="auth-card animate-slideUp">
          <div className="auth-card-header">
            <span className="mono" style={{ color: 'var(--text-dim)' }}>— Reset password</span>
          </div>

          {!isValidToken && !success ? (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              {error ? (
                <>
                  <p style={{ color: 'var(--error, #ff6b6b)', fontSize: '1rem', marginBottom: '1rem' }}>
                    ✗ {error}
                  </p>
                  <p style={{ color: 'var(--text-dim)', marginBottom: '1.5rem' }}>
                    Please request a new password reset link.
                  </p>
                </>
              ) : (
                <p style={{ color: 'var(--text-dim)' }}>
                  Verifying reset link...
                </p>
              )}
            </div>
          ) : success ? (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <p style={{ color: 'var(--accent)', fontSize: '1.1rem', marginBottom: '1rem' }}>
                ✓ Password reset successfully!
              </p>
              <p style={{ color: 'var(--text-dim)' }}>
                Redirecting to sign in...
              </p>
            </div>
          ) : (
            <form onSubmit={handleResetPassword}>
              <div className="field">
                <label className="field-label">New password</label>
                <input
                  type="password"
                  className="field-input"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  minLength={8}
                  required
                />
              </div>
              <div className="field">
                <label className="field-label">Confirm password</label>
                <input
                  type="password"
                  className="field-input"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  minLength={8}
                  required
                />
              </div>
              {error && <div className="auth-error">{error}</div>}
              <button
                type="submit"
                className="btn btn-primary auth-submit"
                disabled={loading}
              >
                {loading ? <span className="spinner" /> : 'Reset password →'}
              </button>
            </form>
          )}

          <div className="auth-switch">
            <button
              className="auth-link"
              onClick={() => navigate('/')}
            >
              Back to sign in
            </button>
          </div>
        </div>

        {/* Philosophy note */}
        <div className="auth-philosophy">
          <div className="auth-philosophy-rule" />
          <p>
            Every capsule incubates for 30 minutes before publishing.<br />
            Responses incubate for 20 minutes.<br />
            You may withdraw or edit it anytime before then.<br />
            <strong>Persistent pseudonym, durable reputation.</strong>
          </p>
        </div>
      </div>
    </div>
  )
}
